import React, {useContext, useState, useEffect, useRef} from "react";
import StyleContext from "../../contexts/StyleContext";
import {greeting, openSource, showChatBot} from "../../portfolio";
import ChatHeader from "./ChatHeader";
import ChatMessage from "./ChatMessage";
import ChatInput from "./ChatInput";
import {REACT_APP_PORTFOLIO_AI_API_URL} from "../../constants";
import {parseSSEStream} from "../../utils";
import {ChatAvatar} from "./ChatAvatar";
import {useAPI} from "../../hooks/useApi";

import "./ChatBot.scss";

export default function ChatBot() {
  const {isDark} = useContext(StyleContext);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      id: 1,
      text: `Hi! I'm ${greeting.name}'s AI assistant. Ask me anything about ${greeting.name}'s experience, projects, or skills.`,
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const botMessageIdRef = useRef(null);
  const {data: profile = {}, submit: fetchProfile} = useAPI("profile");

  useEffect(() => {
    if (openSource.showGithubProfile) {
      fetchProfile();
    }
  }, [fetchProfile]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth"});
  };

  const handleSubmit = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    const userMessage = {
      id: crypto.randomUUID(),
      text: trimmed,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      const response = await fetch(
        `${REACT_APP_PORTFOLIO_AI_API_URL}/api/v1/chat`,
        {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({message: trimmed})
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      for await (const event of parseSSEStream(response.body)) {
        if (event.type === "token") {
          // create one bot message for entire stream
          if (!botMessageIdRef.current) {
            botMessageIdRef.current = crypto.randomUUID();
            setMessages(prev => [
              ...prev,
              {
                id: botMessageIdRef.current,
                text: event.content,
                isUser: false,
                timestamp: new Date()
              }
            ]);
            continue;
          }

          // append text for upcoming chunks
          setMessages(prev =>
            prev.map(msg =>
              msg.id === botMessageIdRef.current
                ? {...msg, text: msg.text + (event.content ?? "")}
                : msg
            )
          );
        }

        if (event.type === "done") {
          console.log("Stream completed");
        }
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "Sorry, something went wrong. Please try again later.",
          isUser: false,
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
      botMessageIdRef.current = null;
    }
  };

  return (
    showChatBot && (
      <>
        {isOpen && (
          <div
            className={
              isDark ? "chatbot-window chatbot-window-dark" : "chatbot-window"
            }
          >
            <ChatHeader
              username={greeting.name}
              avatarUrl={profile?.avatarUrl}
              onClose={() => setIsOpen(false)}
              isDark={isDark}
            />
            <div className="chatbot-messages">
              {messages.map(msg => (
                <ChatMessage
                  key={msg.id}
                  text={msg.text}
                  isUser={msg.isUser}
                  isDark={isDark}
                />
              ))}
              {isLoading && (
                <ChatMessage isUser={false} isDark={isDark} isTyping={true} />
              )}
              <div ref={messagesEndRef} />
            </div>
            <ChatInput
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              isDark={isDark}
            />
          </div>
        )}
        <button
          className={isDark ? "chatbot-fab chatbot-fab-dark" : "chatbot-fab"}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close chat" : "Open chat"}
          style={!isOpen && !avatarLoaded ? {visibility: "hidden"} : undefined}
        >
          {isOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <ChatAvatar
              src={profile?.avatarUrl}
              alt={greeting.name}
              onLoad={() => setAvatarLoaded(true)}
            />
          )}
        </button>
      </>
    )
  );
}
